import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSystemApi } from '../../service/Api.service';
import { AuthContext } from '../auth/AuthContextProvider';
import { NotificationType, showNotification } from '../notifications/Notifications';

type DatabaseLockContextType = {
  isLocked: boolean;
  loading: boolean;
  toggleLock: (locked: boolean) => Promise<void>;
  canModify: boolean;
};

const DatabaseLockContext = createContext<DatabaseLockContextType>({
  isLocked: false,
  loading: true,
  toggleLock: async () => {},
  canModify: false
});

export const useDatabaseLock = () => useContext(DatabaseLockContext);

export const DatabaseLockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const systemApi = useSystemApi();
  const [isLocked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authContext?.isAuthenticated) return;

    const fetchLock = async () => {
      setLoading(true);
      try {
        const res = await systemApi.getDatabaseLock();
        setLocked(res.data);
      } catch {
        showNotification(
          'Nepodařilo se načíst stav databáze.',
          'database-lock',
          NotificationType.ERROR
        );
      } finally {
        setLoading(false);
      }
    };
    fetchLock();
  }, [authContext]);

  const toggleLock = async (locked: boolean) => {
    try {
      setLoading(true);
      await systemApi.setDatabaseLock({ locked });
      setLocked(locked);
      showNotification(
        locked ? 'Databáze byla uzamčena.' : 'Databáze byla odemčena.',
        'database-lock',
        NotificationType.INFO
      );
    } catch (err) {
      showNotification(
        'Nepodařilo se změnit stav databáze.',
        'database-lock',
        NotificationType.INFO
      );
    } finally {
      setLoading(false);
    }
  };

  const canModify = useMemo(() => {
    // if (authContext.hasRole(['superuser', 'manager', 'sysadmin'])) {
    //   return true;
    // }
    return !isLocked;
  }, [authContext, isLocked]);

  return (
    <DatabaseLockContext.Provider value={{ isLocked, loading, toggleLock, canModify }}>
      {children}
    </DatabaseLockContext.Provider>
  );
};
